import { AnimatePresence, motion } from "framer-motion";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { useState } from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { HiChevronDown, HiChevronRight, HiOutlinePencil, HiTrash } from "react-icons/hi";
import { IoEye } from "react-icons/io5";
import { TbArrowsExchange } from "react-icons/tb";

import {
  CourseResourceApi,
  Folder,
  TabWithFolders,
  courseResourcesApi,
  useCourseResourceTab,
} from "api/course-resources";
import { AddResourceModal } from "pages/CourseResources/ResourceModals/AddResourceModal";
import { QUERIES_KEYS } from "queries";
import { FaPlus } from "react-icons/fa";
import { useInavalidate, useToaster } from "utils";
import { useDeleteResourceActions } from "utils/store";
import { EditResourceModal } from "./ResourceModals/EditResourceModal";
import { PreviewResourceModal } from "./ResourceModals/PreviewResourceModal";
import { EditCourseResourceTabModal } from "./TabModals/EditCourseResourceTabModal";
import { ChangeResourceTabModal } from "./ResourceModals/ChangeResourceTabModal";

interface ResourcesFoldersListProps {
  courseId: number;
  courseResourceTabId: number;
}

export const ResourcesFoldersList = ({ courseId, courseResourceTabId }: ResourcesFoldersListProps) => {
  const { tab, isLoading, isFetched, resources } = useCourseResourceTab(courseId, courseResourceTabId);
  if (isLoading) return <PaziaLoader position="center" />;
  return (
    isFetched &&
    tab && <DraggableResourceList tab={tab} key={courseResourceTabId} courseId={courseId} resources={resources} />
  );
};

interface DraggableResourceListProps {
  tab: TabWithFolders;
  courseId: number;
  resources: CourseResourceApi[];
}
const DraggableResourceList = ({ tab, courseId, resources }: DraggableResourceListProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editResource, setEditResource] = useState<CourseResourceApi | null>(null);
  const [moveResource, setMoveResource] = useState<CourseResourceApi | null>(null);
  const [previewResource, setPreviewResource] = useState<CourseResourceApi | null>(null);
  const { setDeleteResource } = useDeleteResourceActions();
  const [editTab, setEditTab] = useState<TabWithFolders | null>(null);
  const { showError, showSuccess } = useToaster();

  const [folders, setFolders] = useState(tab.folders);
  const invalidateResources = useInavalidate(QUERIES_KEYS.COURSE_RESOURCES(Number(courseId)));
  const [savedFolders, setSavedFolders] = useState(JSON.parse(JSON.stringify(tab.folders)));
  const [expandedFolders, setExpandedFolders] = useState(Object.fromEntries(tab.folders.map((f) => [f.name, false])));
  const [isDirty, setIsDirty] = useState(false);
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [folderNameInput, setFolderNameInput] = useState("");

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, type } = result;

    if (type === "folder") {
      const updatedFolders = [...folders];
      const [moved] = updatedFolders.splice(source.index, 1);
      updatedFolders.splice(destination.index, 0, moved);
      setFolders(updatedFolders);
      setIsDirty(true);
      return;
    }

    if (type === "resource") {
      const sourceFolder = folders.find((f) => f.name === source.droppableId);
      const destFolder = folders.find((f) => f.name === destination.droppableId);
      if (!sourceFolder || !destFolder) return;

      const [movedResource] = sourceFolder.resources.splice(source.index, 1);
      destFolder.resources.splice(destination.index, 0, movedResource);

      setFolders([...folders]);
      setIsDirty(true);
    }
  };

  const toggleFolder = (folderName: string) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderName]: !prev[folderName],
    }));
  };

  const handleSave = async () => {
    try {
      await courseResourcesApi.updateFolders(courseId, folders);
      setSavedFolders(JSON.parse(JSON.stringify(folders)));
      setIsDirty(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleReset = () => {
    setFolders(JSON.parse(JSON.stringify(savedFolders)));
    setIsDirty(false);
  };

  const deleteResource = async (folderName: string, resourceId: number) => {
    try {
      await courseResourcesApi.delete(courseId, resourceId);
      invalidateResources();
      showSuccess("Materiał został usunięty.");
      const updatedFolders = folders.map((folder) =>
        folder.name === folderName
          ? { ...folder, resources: folder.resources.filter((r) => r.id !== resourceId) }
          : folder,
      );
      setFolders(updatedFolders);
    } catch (e) {
      console.error(e);
      showError("Nie udało się usunąć materiału.");
    }
  };

  const startEditingFolder = (folder: Folder) => {
    setEditingFolder(folder.name);
    setFolderNameInput(folder.name);
  };

  const saveFolderName = (folder: Folder) => {
    const updatedFolders = folders.map((f) => (f.name === folder.name ? { ...f, name: folderNameInput } : f));
    setFolders(updatedFolders);
    setEditingFolder(null);
    setIsDirty(true);
  };

  const onUpdate = async (data: { name: string; icon: string }) => {
    if (!editTab) return;
    try {
      await courseResourcesApi.updateTab(Number(courseId), editTab.id, data);
      setEditTab(null);
      invalidateResources();
      showSuccess("Zakładka została zaktualizowana.");
    } catch (e) {
      console.error(e);
      showError("Nie udało się zaktualizować zakładki.");
    }
  };
  return (
    <div>
      <div className="flex justify-between rounded-md p-2">
        <div className="flex items-center font-semibold">{tab.name}</div>
        <div className="flex gap-2">
          <PaziaButton type="danger2" onClick={() => setDeleteResource(tab.id)}>
            Usuń
          </PaziaButton>
          <PaziaButton type="secondary" onClick={() => setEditTab(tab)}>
            Edytuj
          </PaziaButton>
          {isDirty && (
            <>
              <PaziaButton type="tertiary" onClick={handleReset}>
                Resetuj zmiany
              </PaziaButton>
              <PaziaButton type="success" onClick={handleSave}>
                Zapisz
              </PaziaButton>
            </>
          )}
        </div>
      </div>
      <div className="space-y-4">
        <div className="flex justify-center">
          <PaziaButton type="primary" innerClassName="flex gap-1 items-center" onClick={() => setIsModalOpen(true)}>
            <FaPlus />
            {folders.length === 0 ? "Dodaj pierwszy materiał" : "Dodaj kolejny materiał"}
          </PaziaButton>
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="folders" type="folder">
            {(provided) => (
              <ul ref={provided.innerRef} {...provided.droppableProps} className="space-y-2 pt-2">
                {folders.map((folder, folderIndex) => (
                  <Draggable key={folder.name} draggableId={folder.name} index={folderIndex}>
                    {(provided) => (
                      <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="rounded-md bg-gray-100 dark:bg-slate-700"
                      >
                        <div
                          {...provided.dragHandleProps}
                          onClick={() => toggleFolder(folder.name)}
                          className="flex cursor-grab items-center justify-between p-3 pr-2 hover:bg-gray-200 dark:hover:bg-slate-600"
                        >
                          <div className="flex items-center gap-2">
                            <button onClick={() => toggleFolder(folder.name)}>
                              {expandedFolders[folder.name] ? (
                                <HiChevronDown size={24} />
                              ) : (
                                <HiChevronRight size={24} />
                              )}
                            </button>
                            {editingFolder === folder.name ? (
                              <input
                                className="rounded border px-2 py-1"
                                value={folderNameInput}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setFolderNameInput(e.target.value)}
                              />
                            ) : (
                              <span>{folder.name}</span>
                            )}
                          </div>
                          {editingFolder === folder.name ? (
                            <PaziaButton
                              btnType="button"
                              type="success"
                              onClick={(e) => {
                                e.stopPropagation();
                                saveFolderName(folder);
                              }}
                              disabled={!folderNameInput}
                            >
                              Zapisz
                            </PaziaButton>
                          ) : (
                            <div className="flex gap-4">
                              <HiOutlinePencil
                                size={20}
                                className="cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingFolder(folder);
                                }}
                              />
                              {folder.resources?.length === 0 && (
                                <HiTrash
                                  className="cursor-pointer text-red-500 hover:text-red-700"
                                  size={20}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const updatedFolders = folders.filter((f) => f.name !== folder.name);
                                    setFolders(updatedFolders);
                                  }}
                                />
                              )}
                            </div>
                          )}
                        </div>

                        {/* Resources List - Expandable with animation */}
                        <AnimatePresence initial={false}>
                          {expandedFolders[folder.name] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="space-y-1 overflow-hidden pb-2 pl-6"
                            >
                              <Droppable droppableId={folder.name} type="resource">
                                {(provided) => (
                                  <ul ref={provided.innerRef} {...provided.droppableProps} className="space-y-1">
                                    {folder.resources.length === 0 ? (
                                      <li className="p-2 italic text-gray-400 dark:text-gray-500">
                                        Brak dodanych materiałów.
                                      </li>
                                    ) : (
                                      folder.resources.map((resource, resourceIndex) => (
                                        <Draggable
                                          key={`${folder.name}-${resource.id}`}
                                          draggableId={`${folder.name}-${resource.id}`}
                                          index={resourceIndex}
                                        >
                                          {(provided, snapshot) => (
                                            <li
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              className="flex cursor-grab items-center justify-between rounded bg-gray-50 p-2 first:mt-2 dark:bg-slate-600 dark:text-white"
                                            >
                                              <motion.div
                                                className="flex-1"
                                                initial={{ scale: 1, opacity: 1 }}
                                                animate={{
                                                  scale: snapshot.isDragging ? 1.05 : 1,
                                                  boxShadow: snapshot.isDragging
                                                    ? "0px 4px 12px rgba(0,0,0,0.2)"
                                                    : "none",
                                                  opacity: snapshot.isDragging ? 0.8 : 1,
                                                }}
                                                transition={{ duration: 0.2 }}
                                              >
                                                {resource.name}
                                              </motion.div>
                                              <IoEye
                                                size={20}
                                                className="mr-4 cursor-pointer"
                                                title="Podgląd"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setPreviewResource(resource);
                                                }}
                                              />
                                              <TbArrowsExchange
                                                size={20}
                                                className="mr-4 cursor-pointer"
                                                title="Przenieś do zakładki"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setMoveResource(resource);
                                                }}
                                              />
                                              <HiOutlinePencil
                                                size={20}
                                                className="mr-4 cursor-pointer"
                                                title="Edytuj"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setEditResource(resources.find((r) => r.id === resource.id) ?? null);
                                                }}
                                              />
                                              <HiTrash
                                                className="cursor-pointer text-red-500 hover:text-red-700"
                                                size={20}
                                                title="Usuń"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  deleteResource(folder.name, resource.id);
                                                }}
                                              />
                                            </li>
                                          )}
                                        </Draggable>
                                      ))
                                    )}
                                    {provided.placeholder}
                                  </ul>
                                )}
                              </Droppable>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>

        <div className="flex flex-col items-center">
          {folders.length === 0 && (
            <div className="p-2 text-center italic text-gray-400">Brak materiałów w tej zakładce.</div>
          )}
        </div>
      </div>

      <AddResourceModal
        isOpen={isModalOpen}
        courseResourceTabId={tab.id}
        onCloseRequest={() => setIsModalOpen(false)}
        courseId={Number(courseId)}
        folderOptions={folders.map((f) => ({ label: f.name, value: f.name }))}
        onSubmit={async (res) => {
          const newResource = res as CourseResourceApi;
          const isNewFolder = !folders.find((f) => f.name === newResource.folder);
          const maxOrder = Math.max(...folders.map((f) => f.folderOrder));
          const updatedFolders = isNewFolder
            ? [
                ...folders,
                { name: newResource.folder ?? "Brak folderu", resources: [newResource], folderOrder: maxOrder + 1 },
              ]
            : folders.map((folder) =>
                folder.name === newResource.folder
                  ? { ...folder, resources: [...folder.resources, newResource] }
                  : folder,
              );
          setFolders(updatedFolders);
        }}
      />
      <PreviewResourceModal
        courseId={courseId}
        onCloseRequest={() => setPreviewResource(null)}
        resource={previewResource}
      />
      <ChangeResourceTabModal
        courseId={courseId}
        onCloseRequest={() => setMoveResource(null)}
        resource={moveResource}
      />

      <EditCourseResourceTabModal tab={editTab} onCloseRequest={() => setEditTab(null)} onSubmit={onUpdate} />
      <EditResourceModal
        resource={editResource}
        onCloseRequest={() => setEditResource(null)}
        courseId={Number(courseId)}
        folderOptions={folders.map((f) => ({ label: f.name, value: f.name }))}
        onSubmit={async (resource) => {
          //update resource row name
          const updatedFolders = folders.map((folder) => ({
            ...folder,
            resources: folder.resources.map((r) => (r.id === resource.id ? resource : r)),
          }));
          setFolders(updatedFolders);
          invalidateResources();
        }}
      />
    </div>
  );
};
